import { Button, Skeleton } from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';
import { ListChecksIcon, PlusIcon } from 'lucide-react';
import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BUILTIN_TRACKER_TEMPLATES,
  trackerTemplatesListQueryOptions,
  type TemplateCard,
} from '@/entities/tracker-template';
import { useCreateTrackerFromTemplate } from '../../hooks/useCreateTrackerFromTemplate';
import { TemplateCoverCard } from '../TemplateCoverCard';

const GRID = 'grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-md';

/**
 * Shown when the viewer can see no trackers. The default boards are provisioned
 * server-side, so in practice this is reached when every tracker in the
 * workspace is private to someone else, or an admin deleted them all.
 */
export function TrackersHubEmptyState({
  workspaceId,
  onStartFromScratch,
}: {
  workspaceId: string;
  onStartFromScratch: () => void;
}) {
  const { t, i18n } = useTranslation('trackers');
  const defaultsId = useId();
  const savedId = useId();
  const fromTemplate = useCreateTrackerFromTemplate({ workspaceId });
  const savedQuery = useQuery({
    ...trackerTemplatesListQueryOptions(workspaceId),
    enabled: Boolean(workspaceId),
  });

  const defaults = useMemo<TemplateCard[]>(
    () =>
      BUILTIN_TRACKER_TEMPLATES.map((builtin) => ({
        source: 'builtin',
        id: `builtin:${builtin.key}`,
        builtin,
      })),
    // `displayName` / `description` are locale getters, so re-read them on a language change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language],
  );
  const saved = useMemo<TemplateCard[]>(
    () =>
      (savedQuery.data ?? []).map((template) => ({ source: 'user', id: template.id, template })),
    [savedQuery.data],
  );

  return (
    <div
      data-testid="trackers-empty-state"
      className="mx-auto flex w-full max-w-[1180px] flex-col gap-3xl px-xl pt-4xl pb-2xl"
    >
      <div className="flex flex-col items-center gap-md text-center">
        <div role="status" className="flex flex-col items-center gap-md">
          <span
            aria-hidden="true"
            className="flex size-14 items-center justify-center rounded-lg bg-secondary text-foreground"
          >
            <ListChecksIcon className="size-6" />
          </span>
          <div className="flex flex-col items-center gap-2xs">
            <h2 className="type-heading-lg text-foreground">
              {t('trackers.trackersHubEmptyState.createYourFirstTracker')}
            </h2>
            <p className="max-w-[460px] type-body-md text-pretty text-muted-foreground">
              {t('trackers.trackersHubEmptyState.description')}
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={onStartFromScratch}>
          <PlusIcon className="size-4" />
          {t('trackers.trackersHubEmptyState.startFromScratch')}
        </Button>
      </div>

      <section aria-labelledby={defaultsId} className="flex flex-col gap-md">
        <h3 id={defaultsId} className="type-label-md text-foreground">
          {t('trackers.templateGalleryDialog.defaultTemplates')}
        </h3>
        <div className={GRID}>
          {defaults.map((card) => (
            <TemplateCoverCard
              key={card.id}
              card={card}
              onPick={fromTemplate.pick}
              disabled={fromTemplate.isPending}
            />
          ))}
        </div>
      </section>

      {savedQuery.isPending ? (
        <div className={GRID} aria-hidden="true">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-56 rounded-lg" />
          ))}
        </div>
      ) : saved.length > 0 ? (
        <section aria-labelledby={savedId} className="flex flex-col gap-md">
          <h3 id={savedId} className="type-label-md text-foreground">
            {t('trackers.templateGalleryDialog.savedTemplates')}
          </h3>
          <div className={GRID}>
            {saved.map((card) => (
              <TemplateCoverCard
                key={card.id}
                card={card}
                onPick={fromTemplate.pick}
                disabled={fromTemplate.isPending}
              />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
